import type {
  ApprovalItem,
  ChatItem,
  MessageItem,
  TextItem,
  ThreadStreamState,
  ToolItem,
  TurnState,
} from '../stream/streamReducer';

/**
 * Transcript rows (SPEC §10).
 *
 * Two shapes the raw item list cannot express:
 *  - consecutive calls of the SAME tool collapse into one row (「讀取檔案 ×7」),
 *    which is the difference between a readable note and seven identical lines;
 *  - each turn ends with a footer carrying the 活動摘要 strip, the cost line and
 *    the 記憶足跡 chips.
 */

export type RenderRow =
  | { readonly kind: 'message'; readonly key: string; readonly item: MessageItem }
  | { readonly kind: 'text'; readonly key: string; readonly item: TextItem }
  | { readonly kind: 'tools'; readonly key: string; readonly items: readonly ToolItem[] }
  | { readonly kind: 'approval'; readonly key: string; readonly item: ApprovalItem }
  | { readonly kind: 'turnFooter'; readonly key: string; readonly turn: TurnState }
  | { readonly kind: 'thinking'; readonly key: string; readonly turnId: string };

export interface ActivityCounts {
  readonly read: number;
  readonly write: number;
  readonly command: number;
  readonly other: number;
  readonly total: number;
}

const READ_TOOLS = new Set(['Read', 'Glob', 'Grep', 'WebFetch', 'WebSearch', 'NotebookRead']);
const WRITE_TOOLS = new Set(['Write', 'Edit', 'MultiEdit', 'NotebookEdit']);

/** Categorises by protocol tool name; the 繁中 labels come from i18n. */
export function activityCounts(items: readonly ToolItem[]): ActivityCounts {
  let read = 0;
  let write = 0;
  let command = 0;
  let other = 0;
  for (const item of items) {
    if (READ_TOOLS.has(item.name)) read += 1;
    else if (WRITE_TOOLS.has(item.name)) write += 1;
    else if (item.name === 'Bash') command += 1;
    else other += 1;
  }
  return { read, write, command, other, total: items.length };
}

function turnHasFooter(turn: TurnState): boolean {
  return (
    turn.usage !== null ||
    turn.workspace !== null ||
    turn.status !== 'running' ||
    turn.error !== null
  );
}

export function buildRows(thread: ThreadStreamState): RenderRow[] {
  const rows: RenderRow[] = [];
  const items: ChatItem[] = [];
  for (const id of thread.order) {
    const item = thread.byId[id];
    if (item !== undefined) items.push(item);
  }

  let currentTurn: string | null = null;
  let pendingTools: ToolItem[] = [];

  const flushTools = (): void => {
    if (pendingTools.length === 0) return;
    const first = pendingTools[0];
    if (first) {
      rows.push({ kind: 'tools', key: `tools:${first.id}`, items: pendingTools });
    }
    pendingTools = [];
  };

  const closeTurn = (turnId: string | null): void => {
    if (turnId === null) return;
    const turn = thread.turns[turnId];
    if (turn && turnHasFooter(turn)) {
      rows.push({ kind: 'turnFooter', key: `foot:${turnId}`, turn });
    }
  };

  for (const item of items) {
    const itemTurn = item.kind === 'message' ? item.turnId : item.turnId;
    if (itemTurn !== currentTurn) {
      flushTools();
      closeTurn(currentTurn);
      currentTurn = itemTurn;
    }

    if (item.kind === 'tool') {
      const last = pendingTools[pendingTools.length - 1];
      if (last && last.name === item.name && item.name !== '') pendingTools.push(item);
      else {
        flushTools();
        pendingTools.push(item);
      }
      continue;
    }

    flushTools();
    if (item.kind === 'message') rows.push({ kind: 'message', key: item.id, item });
    else if (item.kind === 'text') rows.push({ kind: 'text', key: item.id, item });
    else rows.push({ kind: 'approval', key: item.id, item });
  }

  flushTools();
  closeTurn(currentTurn);

  // A started turn that has produced nothing yet shows 「思考中…」 (R21: thinking
  // deltas are not relayed in v1, so silence is the only honest signal).
  const active = thread.activeTurnId;
  if (active !== null) {
    const turn = thread.turns[active];
    const produced = items.some(
      (item) => item.turnId === active && (item.kind === 'text' || item.kind === 'tool'),
    );
    if (turn && !produced) rows.push({ kind: 'thinking', key: `think:${active}`, turnId: active });
  }

  return rows;
}
